import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, TrendingUp, Crown, Target, Sparkles } from 'lucide-react';

const ProductionPlanning = ({
 showProductionPlanning,
 setShowProductionPlanning,
 formData,
 cycleTimeHours,
 outputs,
 quantityPerUnit,
 unitType,
 calculationMethod,
 piecesPerBag,
 totalBatchWeight,
 piecesPerTray,
 numTrays
}) => {
 const cycleTime = parseFloat(cycleTimeHours) || 24;
 const unitsPerBatch = parseFloat(formData.units_per_batch) || 0;
 const sellingPrice = parseFloat(formData.selling_price) || 0;
 const costPerUnit = outputs ? parseFloat(outputs.cost_per_unit) : 0;
 
 // Determine if we should show daily stats (only if cycle time <= 24 hours)
 const showDailyStats = cycleTime <= 24;
 
 // Calculate batches per period for different operating schedules
 const calculateSchedule = (hoursPerDay) => {
 const batchesPerDay = hoursPerDay / cycleTime;
 const unitsPerDay = batchesPerDay * unitsPerBatch;
 const revenuePerDay = unitsPerDay * sellingPrice;
 
 return {
 batchesPerDay: batchesPerDay,
 unitsPerDay: Math.floor(unitsPerDay),
 revenuePerDay: revenuePerDay,
 batchesPerWeek: batchesPerDay * 7,
 unitsPerWeek: Math.floor(unitsPerDay * 7),
 revenuePerWeek: revenuePerDay * 7,
 batchesPerMonth: batchesPerDay * 30,
 unitsPerMonth: Math.floor(unitsPerDay * 30),
 revenuePerMonth: revenuePerDay * 30
 };
 };
 
 const eightHourStats = calculateSchedule(8);
 const twelveHourStats = calculateSchedule(12);
 const sixteenHourStats = calculateSchedule(16);
 const twentyFourHourStats = calculateSchedule(24);
 
 const canCalculate = cycleTime > 0 && unitsPerBatch > 0 && sellingPrice > 0;
 
 // Margin-based pricing strategy calculations
 const calculateMarginStrategy = (targetMargin) => {
 if (!costPerUnit || costPerUnit === 0 || !sellingPrice || sellingPrice === 0) return null;
 
 // Required selling price to achieve target margin
 // Formula: Price = Cost / (1 - Margin)
 const requiredPrice = costPerUnit / (1 - targetMargin);
 
 // Calculate what the actual margin would be at current price
 const currentMargin = ((sellingPrice - costPerUnit) / sellingPrice) * 100;
 
 // Weight adjustment calculation
 // If current price is $20 and we need $25, we can either:
 // 1. Increase price to $25
 // 2. Reduce weight proportionally to maintain $20 price point
 
 // Get the actual weight/quantity per unit based on user's input method
 const unitsPerBatchNum = parseFloat(formData.units_per_batch) || 1;
 let currentWeightPerUnit = 1;
 let weightUnit = 'lbs';
 
 // Check for piece-based calculation first
 if (calculationMethod === 'pieces' && piecesPerBag && totalBatchWeight && piecesPerTray && numTrays) {
 // Calculate weight per unit from piece data
 const totalPieces = (parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0);
 const weightPerPiece = totalPieces > 0 ? parseFloat(totalBatchWeight) / totalPieces : 0;
 currentWeightPerUnit = weightPerPiece * parseFloat(piecesPerBag);
 weightUnit = formData.ingredient_weight_unit || 'lbs';
 
 // Standardize weight unit display
 if (weightUnit === 'oz') {
 weightUnit = 'oz';
 } else if (weightUnit === 'g') {
 weightUnit = 'g';
 } else if (weightUnit === 'kg') {
 weightUnit = 'kg';
 } else {
 weightUnit = 'lbs';
 }
 }
 // Check if user entered quantity_per_unit (for weight-based calculation)
 else if (quantityPerUnit && parseFloat(quantityPerUnit) > 0) {
 currentWeightPerUnit = parseFloat(quantityPerUnit);
 weightUnit = unitType || 'pounds';
 
 // Convert to standard unit for display
 if (weightUnit === 'ounces' || weightUnit === 'oz') {
 weightUnit = 'oz';
 } else if (weightUnit === 'grams' || weightUnit === 'g') {
 weightUnit = 'g';
 } else if (weightUnit === 'pieces') {
 weightUnit = 'pieces';
 } else {
 weightUnit = 'lbs';
 }
 } else {
 // Fallback to calculated weight per unit from total ingredient weight
 const totalWeight = parseFloat(formData.total_ingredient_weight_lb) || 0;
 currentWeightPerUnit = totalWeight > 0 && unitsPerBatchNum > 0 ? totalWeight / unitsPerBatchNum : 1;
 weightUnit = 'lbs';
 }
 
 // Suggested weight per unit if keeping current price
 // New Weight = Current Weight × (Current Price / Required Price)
 const suggestedWeightPerUnit = sellingPrice > 0 && requiredPrice > 0 ? currentWeightPerUnit * (sellingPrice / requiredPrice) : currentWeightPerUnit;
 
 // Calculate weight reduction percentage safely
 const weightReductionPct = currentWeightPerUnit > 0 ? ((currentWeightPerUnit - suggestedWeightPerUnit) / currentWeightPerUnit * 100) : 0;
 
 return {
 targetMargin: (targetMargin * 100).toFixed(0),
 costPerUnit: costPerUnit.toFixed(2),
 requiredPrice: requiredPrice.toFixed(2),
 currentPrice: sellingPrice.toFixed(2),
 currentMargin: isNaN(currentMargin) ? '0.0' : currentMargin.toFixed(1),
 priceDifference: (requiredPrice - sellingPrice).toFixed(2),
 currentWeightPerUnit: currentWeightPerUnit.toFixed(2),
 suggestedWeightPerUnit: suggestedWeightPerUnit.toFixed(2),
 weightReduction: isNaN(weightReductionPct) ? '0.0' : weightReductionPct.toFixed(1),
 weightUnit: weightUnit
 };
 };
 
 const margin25 = calculateMarginStrategy(0.25);
 const margin45 = calculateMarginStrategy(0.45);
 const margin65 = calculateMarginStrategy(0.65);

 return (
 <Card className="border-2 border-green-200 dark:border-green-800">
 <CardHeader className="cursor-pointer" onClick={() => setShowProductionPlanning(!showProductionPlanning)}>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="flex items-center gap-2">
 <TrendingUp className="w-5 h-5 text-green-600" />
 Multi-Batch Production Planning
 </CardTitle>
 <CardDescription>
 Production capacity analysis - {cycleTime}hr cycle time, {unitsPerBatch} units/batch
 </CardDescription>
 </div>
 {showProductionPlanning ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
 </div>
 </CardHeader>
 {showProductionPlanning && (
 <CardContent className="space-y-4">
 {!canCalculate ? (
 <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg text-center">
 <p className="text-sm text-muted-foreground">
 Complete Cycle Time, Units per Batch, and Selling Price to see production projections
 </p>
 </div>
 ) : (
 <>
 {/* Production Planning Table */}
 <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-gradient-to-r from-blue-100 dark:from-blue-900/40 dark:to-cyan-900/40 border-b-2 border-blue-300 dark:border-blue-700">
 <th className="px-4 py-3 text-left font-bold text-blue-900 dark:text-blue-100">
 Production Schedule
 </th>
 {showDailyStats && (
 <>
 <th className="px-4 py-3 text-center font-semibold text-blue-900 dark:text-blue-100">8hr/day</th>
 <th className="px-4 py-3 text-center font-semibold text-blue-900 dark:text-blue-100">12hr/day</th>
 <th className="px-4 py-3 text-center font-semibold text-blue-900 dark:text-blue-100">16hr/day</th>
 </>
 )}
 <th className="px-4 py-3 text-center font-semibold text-blue-900 dark:text-blue-100">24hr/day</th>
 </tr>
 </thead>
 <tbody>
 {/* Daily Production (only if cycle time <= 24 hours) */}
 {showDailyStats && (
 <>
 <tr className="bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-700">
 <td colSpan={5} className="px-4 py-2 font-bold text-orange-900 dark:text-orange-100">
 DAILY PRODUCTION
 </td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Batches per Day</td>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.batchesPerDay.toFixed(2)}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.batchesPerDay.toFixed(2)}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.batchesPerDay.toFixed(2)}</td>
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.batchesPerDay.toFixed(2)}</td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Units per Day</td>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.unitsPerDay.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.unitsPerDay.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.unitsPerDay.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.unitsPerDay.toLocaleString()}</td>
 </tr>
 <tr className="border-b-2 border-orange-300 dark:border-orange-700 bg-orange-100 dark:bg-orange-900/30">
 <td className="px-4 py-2 pl-8 font-semibold">Revenue per Day</td>
 <td className="px-4 py-2 text-center font-bold text-orange-700 dark:text-orange-300">${eightHourStats.revenuePerDay.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-2 text-center font-bold text-orange-700 dark:text-orange-300">${twelveHourStats.revenuePerDay.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-2 text-center font-bold text-orange-700 dark:text-orange-300">${sixteenHourStats.revenuePerDay.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-2 text-center font-bold text-orange-700 dark:text-orange-300">${twentyFourHourStats.revenuePerDay.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 </tr>
 </>
 )}

 {/* Weekly Production */}
 <tr className="bg-primary/5 dark:bg-primary/20/20 border-b border-primary/30 dark:border-primary/30">
 <td colSpan={showDailyStats ? 5 : 2} className="px-4 py-2 font-bold text-primary dark:text-primary pt-3">
 WEEKLY PRODUCTION (7 days)
 </td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Batches per Week</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.batchesPerWeek.toFixed(1)}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.batchesPerWeek.toFixed(1)}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.batchesPerWeek.toFixed(1)}</td>
 </>
 )}
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.batchesPerWeek.toFixed(1)}</td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Units per Week</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.unitsPerWeek.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.unitsPerWeek.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.unitsPerWeek.toLocaleString()}</td>
 </>
 )}
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.unitsPerWeek.toLocaleString()}</td>
 </tr>
 <tr className="border-b-2 border-primary/30 dark:border-primary/30 bg-primary/5 dark:bg-primary/20/30">
 <td className="px-4 py-2 pl-8 font-semibold">Revenue per Week</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-2 text-center font-bold text-primary dark:text-primary">${eightHourStats.revenuePerWeek.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-2 text-center font-bold text-primary dark:text-primary">${twelveHourStats.revenuePerWeek.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-2 text-center font-bold text-primary dark:text-primary">${sixteenHourStats.revenuePerWeek.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 </>
 )}
 <td className="px-4 py-2 text-center font-bold text-primary dark:text-primary">${twentyFourHourStats.revenuePerWeek.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 </tr>

 {/* Monthly Production */}
 <tr className="bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-700">
 <td colSpan={showDailyStats ? 5 : 2} className="px-4 py-2 font-bold text-green-900 dark:text-green-100 pt-3">
 MONTHLY PRODUCTION (30 days)
 </td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Batches per Month</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.batchesPerMonth.toFixed(0)}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.batchesPerMonth.toFixed(0)}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.batchesPerMonth.toFixed(0)}</td>
 </>
 )}
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.batchesPerMonth.toFixed(0)}</td>
 </tr>
 <tr className="border-b border-slate-200 dark:border-slate-700">
 <td className="px-4 py-2 pl-8">Units per Month</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-2 text-center font-medium">{eightHourStats.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{twelveHourStats.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-2 text-center font-medium">{sixteenHourStats.unitsPerMonth.toLocaleString()}</td>
 </>
 )}
 <td className="px-4 py-2 text-center font-medium">{twentyFourHourStats.unitsPerMonth.toLocaleString()}</td>
 </tr>
 <tr className="border-b-2 border-green-300 dark:border-green-700 bg-pro-soft from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40">
 <td className="px-4 py-3 pl-8 font-bold text-green-900 dark:text-green-100">Revenue per Month</td>
 {showDailyStats && (
 <>
 <td className="px-4 py-3 text-center font-bold text-lg text-green-700 dark:text-green-300">${eightHourStats.revenuePerMonth.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-3 text-center font-bold text-lg text-green-700 dark:text-green-300">${twelveHourStats.revenuePerMonth.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 <td className="px-4 py-3 text-center font-bold text-lg text-green-700 dark:text-green-300">${sixteenHourStats.revenuePerMonth.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 </>
 )}
 <td className="px-4 py-3 text-center font-bold text-lg text-green-700 dark:text-green-300">${twentyFourHourStats.revenuePerMonth.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
 </tr>
 </tbody>
 </table>
 </div>

 {/* Margin-Based Pricing Strategy - AI Optimization */}
 {outputs && costPerUnit > 0 && margin25 && margin45 && margin65 && (
 <div className="border border-amber-200 dark:border-amber-700 rounded-lg overflow-hidden">
 <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 px-4 py-3 border-b border-amber-200 dark:border-amber-700">
 <div className="flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-amber-600" />
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">AI-Powered Margin Targeting Strategy</p>
 </div>
 <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
 Optimize your pricing or product size to hit target profit margins
 </p>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-300 dark:border-slate-700">
 <th className="px-4 py-2 text-left font-semibold">Target Margin</th>
 <th className="px-4 py-2 text-center font-semibold">Cost/Unit</th>
 <th className="px-4 py-2 text-center font-semibold">
 <div>Required Price</div>
 <div className="text-[10px] font-normal text-muted-foreground">(Option 1)</div>
 </th>
 <th className="px-4 py-2 text-center font-semibold">
 <div>Alt: Weight/Unit</div>
 <div className="text-[10px] font-normal text-muted-foreground">(Keep ${sellingPrice.toFixed(2)} price)</div>
 </th>
 <th className="px-4 py-2 text-center font-semibold">Strategy</th>
 </tr>
 </thead>
 <tbody>
 {/* 25% Margin - Conservative */}
 <tr className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
 <td className="px-4 py-3">
 <div className="flex items-center gap-2">
 <Target className="w-4 h-4 text-orange-500" />
 <div>
 <p className="font-semibold text-orange-700 dark:text-orange-300">25% Margin</p>
 <p className="text-[10px] text-muted-foreground">Conservative</p>
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">${margin25.costPerUnit}</td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-blue-600">${margin25.requiredPrice}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin25.priceDifference) > 0 ? '+' : ''}{margin25.priceDifference} vs current
 </div>
 </td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-primary">{margin25.suggestedWeightPerUnit} {margin25.weightUnit}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin25.weightReduction) > 0 ? '-' : '+'}{Math.abs(parseFloat(margin25.weightReduction)).toFixed(1)}% vs {margin25.currentWeightPerUnit} {margin25.weightUnit}
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-muted-foreground">
 Value/budget option. Lower risk, easier market entry.
 </td>
 </tr>

 {/* 45% Margin - Standard */}
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-green-50 dark:bg-green-900/10 hover:bg-green-100 dark:hover:bg-green-900/20">
 <td className="px-4 py-3">
 <div className="flex items-center gap-2">
 <Target className="w-4 h-4 text-green-500" />
 <div>
 <p className="font-semibold text-green-700 dark:text-green-300">45% Margin</p>
 <p className="text-[10px] text-muted-foreground">Standard Target</p>
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">${margin45.costPerUnit}</td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-blue-600">${margin45.requiredPrice}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin45.priceDifference) > 0 ? '+' : ''}{margin45.priceDifference} vs current
 </div>
 </td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-primary">{margin45.suggestedWeightPerUnit} {margin45.weightUnit}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin45.weightReduction) > 0 ? '-' : '+'}{Math.abs(parseFloat(margin45.weightReduction)).toFixed(1)}% vs {margin45.currentWeightPerUnit} {margin45.weightUnit}
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-muted-foreground">
 <strong>Recommended:</strong> Balanced profitability and market competitiveness.
 </td>
 </tr>

 {/* 65% Margin - Premium */}
 <tr className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
 <td className="px-4 py-3">
 <div className="flex items-center gap-2">
 <Target className="w-4 h-4 text-amber-500" />
 <div>
 <p className="font-semibold text-amber-700 dark:text-amber-300">65% Margin</p>
 <p className="text-[10px] text-muted-foreground">Premium</p>
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">${margin65.costPerUnit}</td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-blue-600">${margin65.requiredPrice}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin65.priceDifference) > 0 ? '+' : ''}{margin65.priceDifference} vs current
 </div>
 </td>
 <td className="px-4 py-3 text-center">
 <div className="font-bold text-lg text-primary">{margin65.suggestedWeightPerUnit} {margin65.weightUnit}</div>
 <div className="text-[10px] text-muted-foreground">
 {parseFloat(margin65.weightReduction) > 0 ? '-' : '+'}{Math.abs(parseFloat(margin65.weightReduction)).toFixed(1)}% vs {margin65.currentWeightPerUnit} {margin65.weightUnit}
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-muted-foreground">
 Premium positioning. Requires strong brand or unique product.
 </td>
 </tr>

 {/* Current Margin Display */}
 <tr className="bg-cyan-50 dark:bg-cyan-900/20 border-t-2 border-cyan-300 dark:border-cyan-700">
 <td className="px-4 py-3" colSpan="5">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
 <span className="text-sm font-semibold text-cyan-900 dark:text-cyan-100">Your Current Setup:</span>
 </div>
 <div className="text-sm">
 <span className="text-muted-foreground">Price: </span>
 <span className="font-bold">${margin25.currentPrice}</span>
 <span className="text-muted-foreground mx-2">•</span>
 <span className="text-muted-foreground">Weight: </span>
 <span className="font-bold">{margin25.currentWeightPerUnit} {margin25.weightUnit}/unit</span>
 <span className="text-muted-foreground mx-2">•</span>
 <span className="text-muted-foreground">Margin: </span>
 <span className={`font-bold ${parseFloat(margin25.currentMargin) >= 45 ? 'text-green-600' : parseFloat(margin25.currentMargin) >= 25 ? 'text-orange-600' : 'text-red-600'}`}>
 {margin25.currentMargin}%
 </span>
 </div>
 </div>
 </td>
 </tr>
 </tbody>
 </table>
 
 {/* Strategy Notes */}
 <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-xs text-blue-900 dark:text-blue-100 border-t border-blue-200 dark:border-blue-700">
 <p className="font-semibold mb-1">💡 How to Use This Analysis:</p>
 <ul className="list-disc list-inside space-y-0.5 ml-2 text-blue-800 dark:text-blue-200">
 <li><strong>Option 1 (Price):</strong> Increase your selling price to the "Required Price" to hit the target margin</li>
 <li><strong>Option 2 (Weight):</strong> Keep your current price but reduce weight per unit to the suggested amount</li>
 <li>Market positioning matters: Premium brands can command higher margins, value brands need tighter margins</li>
 <li>Test different price points with your target customers before committing to production</li>
 </ul>
 </div>
 </div>
 )}

 {/* Production Planning Tips */}
 <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
 <p className="font-semibold text-sm mb-2">💡 Production Planning Tips:</p>
 <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground ml-2">
 <li><strong>8hr schedule:</strong> Standard daytime shift (e.g., 8am-4pm) - Best for part-time operations</li>
 <li><strong>12hr schedule:</strong> Extended day shift (e.g., 8am-8pm) - Good work-life balance</li>
 <li><strong>16hr schedule:</strong> Double shift operation (e.g., 6am-10pm) - Higher utilization</li>
 <li><strong>24hr schedule:</strong> Continuous operation - Maximum capacity, requires overnight runs</li>
 <li>Candy/small items with short cycle times benefit most from extended hours</li>
 <li>Consider prep time, cleanup, labor availability, and workspace access when planning</li>
 <li>Monthly figures assume consistent daily production (adjust for your actual schedule)</li>
 </ul>
 
 {/* Upgrade to Pro CTA */}
 <div className="mt-4 p-4 bg-pro-soft dark:/30 dark:to-indigo-900/30 rounded-lg border-2 border-primary/30 dark:border-primary/30">
 <div className="flex items-start gap-3">
 <div className="bg-primary text-white p-2 rounded-lg">
 <Crown className="w-5 h-5" />
 </div>
 <div className="flex-1">
 <p className="font-bold text-sm text-primary dark:text-primary mb-1">Unlock Advanced Business Tools</p>
 <p className="text-xs text-primary dark:text-primary mb-3">
 Access Tier 1 Pro features including advanced cost modeling, multi-location planning, inventory optimization, and expert business consultation.
 </p>
 <Button size="sm" className="bg-primary hover:bg-primary/90 text-white">
 <Crown className="w-4 h-4 mr-2" />
 Upgrade to Pro
 </Button>
 </div>
 </div>
 </div>
 </div>
 </>
 )}
 </CardContent>
 )}
 </Card>
 );
};

export default ProductionPlanning;
